import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { DataGrid } from '@mui/x-data-grid';
import { getMyEarnings, getStripeStatus } from '../services/internApi';
import { formatCurrency, formatDate } from '../utils/formatters';
import StripeConnectBanner from '../components/StripeConnectBanner';

export default function EarningsPage() {
  const [data, setData] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      getMyEarnings()
        .then((d) => setData(d))
        .catch((err) => setError(err?.response?.data?.error || 'Failed to load earnings')),
      getStripeStatus()
        .then((s) => setStripe(s))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const summary = data?.summary || {};
  const lineItems = (data?.line_items || []).map((l, i) => ({ id: i, ...l }));

  const cards = [
    { label: 'Total Earned', value: formatCurrency(summary.total_earned), color: '#16a34a', sub: `${summary.completed_count || 0} paid assessments` },
    { label: 'Pending', value: formatCurrency(summary.pending), color: '#d97706', sub: `${summary.upcoming_count || 0} upcoming` },
    { label: 'Referral Bonuses', value: formatCurrency(summary.referral_earned), color: '#7c3aed', sub: `${summary.referral_count || 0} referrals` },
    { label: 'Lifetime Value', value: formatCurrency(summary.total_lifetime), color: '#2563eb', sub: 'Earned + pending' },
  ];

  const statusColor = (s) => {
    if (s === 'paid') return 'success';
    if (s === 'failed' || s === 'blocked') return 'error';
    return 'warning';
  };
  const statusLabel = (s) => {
    if (s === 'paid') return 'Paid';
    if (s === 'failed') return 'Failed';
    if (s === 'blocked') return 'Blocked';
    return 'Pending';
  };

  const columns = [
    { field: 'time_slot', headerName: 'Date', flex: 1.2, valueFormatter: (p) => formatDate(p.value) },
    {
      field: 'service_type',
      headerName: 'Service',
      flex: 0.8,
      valueFormatter: (p) => (p.value === 'business' ? 'Business' : 'Home'),
    },
    { field: 'type', headerName: 'Type', flex: 0.8, valueFormatter: (p) => p.value === 'assessment' ? 'Assessment' : 'Referral' },
    { field: 'amount', headerName: 'Amount', flex: 0.7, valueFormatter: (p) => formatCurrency(p.value) },
    {
      field: 'status',
      headerName: 'Payout',
      flex: 0.8,
      renderCell: (p) => (
        <Chip
          label={statusLabel(p.value)}
          size="small"
          color={statusColor(p.value)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Earnings</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        $140 per Home assessment. $240 per Business assessment. $10 bonus per referred client.
      </Typography>

      <StripeConnectBanner
        payoutsEnabled={stripe?.stripe_payouts_enabled}
        graceDaysRemaining={stripe?.stripe_grace_days_remaining}
        requirementsDue={stripe?.stripe_requirements_due}
      />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={6} lg={3} key={c.label}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
              <Typography variant="body2" color="text.secondary" noWrap>{c.label}</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: c.color,
                  my: 1,
                  fontSize: 'clamp(1.05rem, 4vw, 2.125rem)',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  
                }}
                title={String(c.value)}
              >
                {c.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Earnings History</Typography>
        {lineItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No earnings yet. Accept your first booking to get started.
          </Typography>
        ) : (
          <Box sx={{ height: 450 }}>
            <DataGrid
              rows={lineItems}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
